import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import { Route } from "src/shared/constants/route";
import { useEventContext, useUserContext } from "src/shared/contexts";
import { ArrowRight } from "src/static/icons";
import { mediaQueries } from "src/theme/deviceBreakpoints";
import {
  formatDevpostCountdown,
  formatMostSignificantUnit,
  getJudgingTopBannerContent,
  JUDGING_BANNER_ICON,
  JudgingBannerVariant,
} from "src/views/hacker/judging/judging-info/constants";
import { useGetMyJudgingInfoQuery } from "src/views/hacker/judging/judging-info/graphql/getMyJudgingInfo.generated";
import { isMissingHackerEventAttendee } from "src/views/hacker/judging/judging-info/missingEventAttendee";

import Link from "../Link";

const COUNTDOWN_LABEL: Record<"draft" | "final", string> = {
  draft: "Draft Due in",
  final: "Final Submission Due in",
};

export const JUDGING_BANNER_HEIGHT = 42;

const JudgingBanner: React.FC = () => {
  const { isAuthenticated } = useUserContext();
  const { judging } = useEventContext();

  const { data, loading: isJudgingInfoLoading } = useGetMyJudgingInfoQuery({
    skip: !isAuthenticated,
  });

  const [now, setNow] = useState(() => new Date());

  const hasNoEventAttendee = isMissingHackerEventAttendee(
    data?.myJudgingInfo,
    !isJudgingInfoLoading && Boolean(data)
  );

  const content =
    isJudgingInfoLoading || !data
      ? null
      : getJudgingTopBannerContent(
          data.myJudgingInfo?.status,
          now,
          judging.draftDeadline,
          judging.finalDeadline,
          hasNoEventAttendee
        );
  const hasCountdown = Boolean(content?.countdown);

  useEffect(() => {
    if (!hasCountdown) return undefined;

    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [hasCountdown]);

  // Avoid treating an unresolved or failed query as a real no-submission
  // status. The judging info page likewise waits for this query to resolve.
  if (!content) return null;

  const BannerIcon = JUDGING_BANNER_ICON[content.variant];

  return (
    <Banner role="status" $variant={content.variant}>
      {/* Desktop layout */}
      <Side>
        <IconFrame aria-hidden>
          <BannerIcon />
        </IconFrame>
        <Title>{content.title}</Title>
      </Side>

      {content.countdown && (
        <Countdown>
          {COUNTDOWN_LABEL[content.countdown.mode]}
          <strong>
            {formatDevpostCountdown(content.countdown.deadline, now)}
          </strong>
        </Countdown>
      )}

      <Side>
        <StyledLink href={Route.HACKER_JUDGING_INFO}>
          View Judging Info
          <ArrowRight aria-hidden />
        </StyledLink>
      </Side>

      {/* Mobile layout — single line: "Title · Xh left" */}
      <MobileRow>
        <span>{content.title}</span>
        {content.countdown && (
          <>
            <DotSeparator aria-hidden>·</DotSeparator>
            <strong>
              {formatMostSignificantUnit(content.countdown.deadline, now)} left
            </strong>
          </>
        )}
      </MobileRow>
    </Banner>
  );
};

const variantColor = (variant: JudgingBannerVariant) => css`
  color: ${({ theme }) =>
    variant === JudgingBannerVariant.Danger
      ? theme.globalConstants.color.textDanger
      : variant === JudgingBannerVariant.Success
      ? theme.globalConstants.color.textSuccess
      : variant === JudgingBannerVariant.Warning
      ? theme.globalConstants.color.textWarning
      : theme.globalConstants.color.brandPrimary};
`;

const variantBackground = (variant: JudgingBannerVariant) => css`
  background: ${({ theme }) =>
    variant === JudgingBannerVariant.Danger
      ? theme.globalConstants.color.backgroundDangerLight
      : variant === JudgingBannerVariant.Success
      ? theme.globalConstants.color.greenSecondary
      : variant === JudgingBannerVariant.Warning
      ? theme.globalConstants.color.backgroundWarningLight
      : theme.globalConstants.color.backgroundPrimaryLight};
`;

const Banner = styled.div<{ $variant: JudgingBannerVariant }>`
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  height: ${JUDGING_BANNER_HEIGHT}px;
  box-sizing: border-box;
  padding: 8px 32px;
  ${({ $variant }) => variantBackground($variant)}
  ${({ $variant }) => variantColor($variant)}

  ${mediaQueries.tablet} {
    padding: 8px 16px;
    height: auto;
    min-height: ${JUDGING_BANNER_HEIGHT}px;
    justify-content: center;
  }
`;

const Side = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  ${mediaQueries.tablet} {
    display: none;
  }
`;

const IconFrame = styled.span`
  display: inline-flex;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }

  ${mediaQueries.tablet} {
    display: none;
  }
`;

const Title = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700;
  font-size: 16px;
  line-height: 1.6;
  white-space: nowrap;
`;

const Countdown = styled.div`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 500;
  font-size: 16px;
  line-height: 1.5;

  strong {
    font-weight: 700;
    display: inline-block;
    min-width: 12ch;
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum";
    text-align: left;
  }

  ${mediaQueries.tablet} {
    display: none;
  }
`;

const DotSeparator = styled.span`
  margin: 0 10px;
`;

const MobileRow = styled.p`
  display: none;
  margin: 0;

  ${mediaQueries.tablet} {
    display: block;
    font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
    font-weight: 500;
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    strong {
      font-weight: 700;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
    }
  }
`;

const StyledLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-weight: 700 !important;
  font-size: 16px !important;
  color: inherit !important;
  white-space: nowrap;

  svg {
    width: 24px;
    height: 24px;
  }

  ${mediaQueries.tablet} {
    display: none;
  }
`;

export default JudgingBanner;
